import { ChangeDetectorRef, Component, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { CVLocalizationService } from '../../../reusables/services/cv-localization.service';
import { GridApi, GridOptions, GridReadyEvent, ICellRendererParams, IRowModel, RowNode, ValueGetterParams } from 'ag-grid-community';
import { AddEditModuleAccessByLoginParameters, AddEditPageAccessByLoginParameters, DashboardModuleAccess, DashboardModuleResponsive, DashboardModulesLegacy, GetModuleAccessByLoginIDParameters, pageAccessForUser, PageDTO, pagePackage, ProfileDTO, SalesPackage, User, WidgetsDisplayData, WidgetsTableData } from '../user-admin.model';
import { UserAdminService } from '../user-admin.service';
import { PageAccessCellRenderer } from './page-access-cell/page-access-cell.component';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { WidgetAccessCellRendererComponent } from './widget-access-cell-renderer/widget-access-cell-renderer.component';
import { cloneDeep } from 'lodash';
import { AgGridAngular } from 'ag-grid-angular';

@Component({
    selector: 'cv-access-useradmin',
    templateUrl: './access-useradmin.component.html',
    styleUrls: ['./access-useradmin.component.scss'],
    encapsulation: ViewEncapsulation.None //allow styling ag-grid
})
export class AccessUseradminComponent implements OnInit {
    @Input() public selectedUser: User;
    @Input() public selectedProfile: ProfileDTO;
    @Input() public viewOnly: boolean;

    @ViewChild('accessUserAdminAccessGrid') agGridAccess: AgGridAngular;
    @ViewChild('accessUserAdminWidgetsGrid') agGridWidgets: AgGridAngular;

    public search = "";
    public firstRowGroup: string;
    public localizationDic: Map<string, string> = new Map();
    public localizationAgGridDic: Map<string, string> = new Map();
    public filteredPageList: PageDTO[] = [];
    public originalPageList: PageDTO[] = [];
    public responsiveWidgets: DashboardModuleResponsive[] = [];
    public widgetAccess: DashboardModuleAccess[] = [];
    public SalesPackages: SalesPackage[] = [];
    public widgetsDisplay: WidgetsDisplayData[] = [];
    public filteredWidgetsDisplay: WidgetsDisplayData[] = [];
    public changedWidgets: WidgetsTableData[] = [];

    public accessGridOptions: GridOptions = null;
    public widgetsGridOptions: GridOptions = null;
    public tab: string;
    public editMode: boolean = false;

    public hasPages: boolean = false;
    public hasWidgets: boolean = false;
    public disableSaveButton: boolean = true;

    public gridApi: GridApi;

    public localizationNames: Map<string, string>;
    public localizationDescriptions: Map<string, string>;
    public localizationUsers: Map<string, string>;

    constructor(
        private service: UserAdminService,
        private AgGridHelperService: AgGridHelperService,
        private cdr: ChangeDetectorRef,
        private cvLocalizationFactory: CVLocalizationService
    ) { }

    public async ngOnInit() {
        this.localizationNames = await this.cvLocalizationFactory.GetSection("Page_Names");
        this.localizationDescriptions = await this.cvLocalizationFactory.GetSection("Page_Descriptions");
        this.localizationNames = await this.cvLocalizationFactory.GetSection("Module_Names");
        this.localizationDescriptions = await this.cvLocalizationFactory.GetSection("Module_Descriptions");
        this.localizationUsers = await this.cvLocalizationFactory.GetSection("Users");

        //set the page to the "Pages" tab
        this.tab = 'tab1';
        this.cdr.detectChanges();

        //First I have to load all the pages into one big array
        await this.loadPages();

        this.SalesPackages = await this.service.loadSalesPackages();

        await this.loadWidgets();

        //then I capture the first rowGroup key essentially that way I can have that open on startup
        this.firstRowGroup = this.filteredPageList[0].SalesPackageName;

        //Then I load the pageAccess for the selected user both their login access and profile access
        await this.loadPageAccess();

        //load language keys
        this.localizationDic = await this.cvLocalizationFactory.GetSection("Users");
        this.localizationAgGridDic = await this.cvLocalizationFactory.GetSection("AgGrid");

        //Then I set up the ag-grid
        this.setUpAccessGridOptions();

        //set the ag-grid data
        this.accessGridOptions.rowData = this.filteredPageList;

        this.hasPages = true;
    }

    /**
    * This function set's up the ag-grid for the pages tab. Also set's the gridoptions  
    * */
    public setUpAccessGridOptions() {
        this.accessGridOptions = {
            columnDefs: [
                {
                    field: 'SalesPackageName',
                    rowGroup: true,
                    hide: true,
                },
                {
                    field: 'DisplayName',
                    cellRenderer: PageAccessCellRenderer,
                    cellRendererParams: {},
                    width: 780,
                },
            ],
            headerHeight: 0,
            context: {
                enableChanges: () => this.enableChangesButton(),
                editMode: this.editMode,
            },
            groupDisplayType: 'groupRows',
            groupRowRenderer: 'agGroupCellRenderer',
            groupRowRendererParams: {
                innerRenderer: function (params: ICellRendererParams) {
                    return params.node.key;
                },
                suppressCount: true,
            },
            rowHeight: 40,
            animateRows: true,
            rowStyle: { background: 'white' },
            rowData: [],
            suppressContextMenu: true,
            suppressRowHoverHighlight: true,
            defaultColDef: {
                sortable: true,
                filter: true,
                autoHeight: true,
            },
            groupHeaderHeight: 0,
            overlayNoRowsTemplate: `<p>${this.localizationAgGridDic.get('noRowsToShow')}</p>`,
            isExternalFilterPresent: () => {
                return this.search.length > 0
            },
            doesExternalFilterPass: (node: any) => {
                return this.AgGridHelperService.doesExternalFilterPassIncludeHidden(node, this.search);
            },
        };
    }

    /**
    * This function set's up the ag-grid for the widgets tab. Also set's the gridoptions  
    * */
    public setupWidgetsGridOptions() {
        this.widgetsGridOptions = {
            columnDefs: [
                {
                    field: 'SalesPackageName',
                    rowGroup: true,
                    hide: true,
                },
                {
                    field: 'data',
                    cellRenderer: WidgetAccessCellRendererComponent,
                    cellRendererParams: {},
                    width: 780,
                },
            ],
            headerHeight: 0,
            context: {
                enableChanges: () => this.enableChangesButton(),
                editMode: this.editMode,
                changedWidgets: this.changedWidgets,
                searchText: this.search,
            },
            groupDisplayType: 'groupRows',
            groupRowRenderer: 'agGroupCellRenderer',
            groupRowRendererParams: {
                innerRenderer: function (params: ICellRendererParams) {
                    return params.node.key;
                },
                suppressCount: true,
            },
            rowHeight: 40,
            animateRows: true,
            rowStyle: { background: 'white' },
            suppressRowHoverHighlight: true,
            rowData: [],
            suppressContextMenu: true,
            defaultColDef: {
                sortable: true,
                filter: true,
                autoHeight: true,
            },
            groupHeaderHeight: 0,
            overlayNoRowsTemplate: `<p>${this.localizationAgGridDic.get('noRowsToShow')}</p>`
        };
    }

    /**
    * Async function to load the pages and child pages
    * */
    public async loadPages() {
        let pageGroups = await this.service.loadPagesAndChildPages();

        let pageList: PageDTO[] = [];

        for (let i = 0; i < pageGroups.length; i++) {
            for (let j = 0; j < pageGroups[i].Pages.length; j++) {
                //Here for some reason the pages are loading in with weird pageAccess data. I'm removing that here by just setting them to false
                //I load in the page access later
                pageGroups[i].Pages[j].CreateAvailable = false;
                pageGroups[i].Pages[j].WriteAvailable = false;
                pageGroups[i].Pages[j].ReadAvailable = false;

                pageGroups[i].Pages[j].DisplayName = this.getLocalizedName(pageGroups[i].Pages[j].PageName, pageGroups[i].Pages[j].DisplayName);;
                pageGroups[i].Pages[j].Description = this.getLocalizedDescription(pageGroups[i].Pages[j].PageName, pageGroups[i].Pages[j].Description);

                for (let a = 0; a < pageGroups[i].Pages[j].childPages.length; a++) {
                    pageGroups[i].Pages[j].childPages[a].DisplayName = this.getLocalizedName(pageGroups[i].Pages[j].childPages[a].PageName, pageGroups[i].Pages[j].childPages[a].DisplayName);
                    pageGroups[i].Pages[j].childPages[a].Description = this.getLocalizedDescription(pageGroups[i].Pages[j].childPages[a].PageName, pageGroups[i].Pages[j].childPages[a].Description);
                }

                pageList.push(pageGroups[i].Pages[j]);

                if (pageGroups[i].Pages[j].childPages.length > 0) {
                    for (let a = 0; a < pageGroups[i].Pages[j].childPages.length; a++) {
                        pageGroups[i].Pages[j].childPages[a].IsChildPage = true;
                        pageList.push(pageGroups[i].Pages[j].childPages[a]);
                    }
                }
            }
        }

        this.filteredPageList = pageList;
    }

    /**
    * Async Function to load responsive and legacy dashboardModules
    * */
    public async loadWidgets() {
        this.responsiveWidgets = await this.service.loadResponsiveDashboardModules();

        let params = {} as GetModuleAccessByLoginIDParameters
        params.LoginID = this.selectedUser.LoginID;
        params.profileDto = this.selectedProfile;
        this.widgetAccess = await this.service.loadDashboardModulesAccess(params);
    }

    /**
    * Async function to load both role and individual user access to pages
    * */
    public async loadPageAccess() {
        //this loads the page access for the selected user by their login
        let temp = await this.service.loadPageAccessByLogin(this.selectedUser.LoginID);

        //This is some for loops to copy over the pageAccess to the list of pages
        for (let i = 0; i < temp.length; i++) {
            for (let j = 0; j < this.filteredPageList.length; j++) {
                if (this.filteredPageList[j].PageID == temp[i].PageID) {
                    this.filteredPageList[j].CreateAvailable = temp[i].CreateAvailable;
                    this.filteredPageList[j].ReadAvailable = temp[i].ReadAvailable;
                    this.filteredPageList[j].WriteAvailable = temp[i].WriteAvailable;
                    break;
                }
            }
        }

        //this loads in the pageAccess for the selected users role and then I combine it all to the pageList
        temp = await this.service.loadPageAccess(this.selectedProfile);

        for (let i = 0; i < temp.length; i++) {
            for (let j = 0; j < this.filteredPageList.length; j++) {
                if (this.filteredPageList[j].PageID == temp[i].PageID) {
                    this.filteredPageList[j].RoleCreateAvailable = temp[i].CreateAvailable;
                    this.filteredPageList[j].RoleReadAvailable = temp[i].ReadAvailable;
                    this.filteredPageList[j].RoleWriteAvailable = temp[i].WriteAvailable;
                    break;
                }
            }
        }

        this.originalPageList = cloneDeep(this.filteredPageList);
    }

    /**
    * Function to change the edit mode for the front end. Also updates context for the ag-grids 
    * */
    public editChange() {
        if (this.editMode == true) {
            this.editMode = false;
        } else {
            this.editMode = true;
        }

        if (this.accessGridOptions != null) {
            this.accessGridOptions.context.editMode = this.editMode;
            if (this.agGridAccess?.api != null) {
                this.agGridAccess.api.redrawRows();
            }
        }

        if (this.widgetsGridOptions != null) {
            this.widgetsGridOptions.context.editMode = this.editMode;
            this.widgetsGridOptions.context.changedWidgets = this.changedWidgets;
            this.widgetsGridOptions.context.searchText = this.search;
            if (this.agGridWidgets?.api != null) {
                this.agGridWidgets.api.redrawRows();
            }
        }
    }

    /**
    * Function for when Cancel is clicked refreshes page access, widget access, and the front end 
    * */
    public async cancelButtonClick() {
        this.hasPages = false;
        this.hasWidgets = false;
        this.disableSaveButton = true;
        this.cdr.detectChanges();

        await this.loadPageAccess();
        await this.loadWidgets();
        this.loadWidgetData();

        if (this.tab == 'tab1') {
            this.hasPages = true;
        } else {
            this.hasWidgets = true;
        }
        this.cdr.detectChanges();

        this.editChange();
    }

    /**
    * Async function for when the Save Changes button is clicked, Checks for both changed pages and widgets and then calls the respective service
    * function to save the changed widget and page access. 
    * */
    public async saveChanges() {
        //here I need to compare the filteredPageList to the PageList for any changes then I can add that serviceParameters 
        let tempList: PageDTO[] = [];

        for (let i = 0; i < this.filteredPageList.length; i++) {
            if (this.filteredPageList[i].isChanged) {

                let comparison = this.originalPageList.find(x => x.PageID == this.filteredPageList[i].PageID);
                if (comparison.CreateAvailable != this.filteredPageList[i].CreateAvailable ||
                    comparison.ReadAvailable != this.filteredPageList[i].ReadAvailable ||
                    comparison.WriteAvailable != this.filteredPageList[i].WriteAvailable) {
                    tempList.push(this.filteredPageList[i]);
                }
                this.filteredPageList[i].isChanged = false;
            }
        }

        if (tempList.length > 0) {
            let serviceParameters = {} as AddEditPageAccessByLoginParameters;

            serviceParameters.PageDtos = tempList;
            //Here i need to check if they already have pageAccess by Login
            //That is going to be determined by a flag that I set on each one when I load in the page access.  

            serviceParameters.LoginID = this.selectedUser.LoginID;

            await this.service.updatePageAccess(serviceParameters);
            this.originalPageList = cloneDeep(this.filteredPageList);
        }

        //this section is for saving the dashboardmodule acesses
        let dashboardModuleAccesses: DashboardModuleAccess[] = [];
        this.changedWidgets.forEach(widget => {

            let moduleAccess: DashboardModuleAccess = {
                ProfileID: 0,
                LoginID: this.selectedUser.LoginID,
                ResponsiveModuleID: -1,
                LegacyModuleID: -1
            };

            if (!widget.IsLegacy) {
                moduleAccess.ResponsiveModuleID = widget.ModuleID;
            } else {
                moduleAccess.LegacyModuleID = widget.ModuleID;
            }

            if (!widget.ReadAccess) {
                moduleAccess.ReadAccess = false;
            }

            dashboardModuleAccesses.push(moduleAccess);
        });

        if (dashboardModuleAccesses.length > 0) {
            let apiParams: AddEditModuleAccessByLoginParameters = {
                ModuleAccesses: dashboardModuleAccesses,
                LoginID: this.selectedUser.LoginID
            }

            await this.service.updateModuleAccessByLoginID(apiParams);
        }

        this.changedWidgets = [];
        for (let i = 0; i < this.filteredPageList.length; i++) {
            if (this.filteredPageList[i].isChanged) {
                this.filteredPageList[i].isChanged = false;
            }
        }

        this.disableSaveButton = true;
        this.editChange();
    }

    /**
    * Function to update the save changes button on the front end
    * */
    public enableChangesButton() {
        if (this.disableSaveButton) {
            this.disableSaveButton = false;
        }
    }

    /**
    * Function to filter the ag-grid rows depending on the search. Widgets 
    * */
    public applyFilter() {
        if (this.tab == "tab1") {
            if (this.agGridAccess?.api)
                this.agGridAccess.api.onFilterChanged();

            if (this.agGridAccess?.api?.getDisplayedRowCount() == 0) {
                this.agGridAccess.api.showNoRowsOverlay();
            } else {
                this.agGridAccess?.api?.hideOverlay();

                this.agGridAccess?.api?.forEachNode((node) => {
                    if (this.search != "" && node.key != null) {
                        node.setExpanded(true);
                    } else if (this.search == "" && node.key == this.firstRowGroup) {
                        node.setExpanded(true);
                        return;
                    } else if (this.search == "" && node.key != null) {
                        node.setExpanded(false);
                        return;
                    }
                });

            }
        }
        else {
            this.widgetsGridOptions.context.searchText = this.search;

            this.filterWidgetCategory();

            let params = { columns: ['data'] };
            let instances = this.agGridWidgets?.api.getCellRendererInstances(params) as any[];
            instances.forEach((instance) => {
                instance.refresh(this.search);
            })

            document.getElementById('searchBox').focus();
        }

        return;
    }

    /**
    * Function for when the X button on the search bar is clicked
    * */
    public clearFilter() {
        this.search = "";
        this.applyFilter(); //notify table of the update
    }

    /**
    * Function to open the first rowGroup in the ag-grid. get's called after the page loads
    * @param params 
    * */
    public onGridReady(params: GridReadyEvent) {
        this.gridApi = params.api;

        this.gridApi.forEachNode((node) => {
            if (this.search != "" && node.key != null) {
                node.setExpanded(true);
            } else if (node.key == this.firstRowGroup) {
                node.setExpanded(true);
                return;
            }
        });
    }

    /**
     * Returns localized string from the Users localization section.
     * @param key Key for localized string to retrieve
     * @param defaultValue Value to return if error occurs
     * @param inserts Values to insert into the localized (or default) string; the first will replace {0}, the second will replace {1}, etc.
     */
    public getLocalizedUsersString(key: string, defaultValue: string, ...inserts: string[]) {
        let baseString = this.cvLocalizationFactory.getCachedText("Users", key, defaultValue);

        for (let i = 0; i < inserts.length; i++) {
            baseString = baseString.replace(`{${i}}`, inserts[i]);
        }

        return baseString;
    }

    /**
     * Returns localized string from the Page_Names localization section.
     * @param key Key for localized string to retrieve
     * @param defaultValue Value to return if error occurs
     * @param inserts Values to insert into the localized (or default) string; the first will replace {0}, the second will replace {1}, etc.
     */
    public getLocalizedName(key: string, defaultValue: string, ...inserts: string[]) {
        let baseString = this.cvLocalizationFactory.getCachedText("Page_Names", key, defaultValue);

        for (let i = 0; i < inserts.length; i++) {
            baseString = baseString.replace(`{${i}}`, inserts[i]);
        }

        return baseString;
    }

    /**
     * Returns localized string from the Page_Descriptions localization section.
     * @param key Key for localized string to retrieve
     * @param defaultValue Value to return if error occurs
     * @param inserts Values to insert into the localized (or default) string; the first will replace {0}, the second will replace {1}, etc.
     */
    public getLocalizedDescription(key: string, defaultValue: string, ...inserts: string[]) {
        let baseString = this.cvLocalizationFactory.getCachedText("Page_Descriptions", key, defaultValue);

        for (let i = 0; i < inserts.length; i++) {
            baseString = baseString.replace(`{${i}}`, inserts[i]);
        }

        return baseString;
    }

    public getLocalizedString(key: string, defaultValue: string, ...inserts: string[]) {
        let baseString = this.cvLocalizationFactory.getCachedText("Users", key, defaultValue);

        for (let i = 0; i < inserts.length; i++) {
            baseString = baseString.replace(`{${i}}`, inserts[i]);
        }

        return baseString;
    }

    /**
    * Function for when the widgets tab is clicked. it loads in the widget access and then sets the grid options context
    * */
    public widgetsTabClicked() {
        if (this.tab == 'tab2')
            return;

        this.tab = 'tab2';
        this.hasPages = false;
        this.cdr.detectChanges();

        if (this.widgetsDisplay.length == 0) {
            this.loadWidgetData();
        }

        this.widgetsGridOptions.context.editMode = this.editMode;
        this.widgetsGridOptions.context.changedWidgets = this.changedWidgets;
        this.widgetsGridOptions.context.searchText = this.search;
        this.filterWidgetCategory();
        this.agGridWidgets?.api?.refreshCells();
    }

    /**
    * Function for when the pages tab is clicked. it just refreshes the front end
    * */
    public pagesTabClicked() {
        if (this.tab == 'tab1')
            return;

        this.tab = 'tab1';

        this.hasWidgets = false;
        this.cdr.detectChanges();
        this.hasPages = true;
        this.cdr.detectChanges();

        if (this.filteredPageList.length >= 0) {
            this.firstRowGroup = this.filteredPageList[0].SalesPackageName;
        }

        this.applyFilter();
    }

    /**
    * Function to load responsive and legacy widget access. it packages both of those into row's for the ag-grid. Then sets the rowdata for the widgets ag-grid
    * */
    public loadWidgetData() {
        this.widgetsDisplay = [];

        this.SalesPackages.forEach(salePackage => {
            let newDisplayData: WidgetsDisplayData = {
                SalesPackageID: salePackage.PackageID,
                SalesPackageName: salePackage.PackageName,
                TableData: []
            };

            this.widgetsDisplay.push(newDisplayData);
        });

        this.responsiveWidgets.forEach(widget => {
            let newDisplay: WidgetsTableData = {
                ModuleID: widget.ModuleID,
                ModuleName: widget.ModuleName,
                ModuleDescription: widget.ModuleDescription,
                SalesPackageID: widget.PackageID,
                ReadAccess: widget.ReadAccess,
                IsLegacy: false
            }

            let accessIndex = this.widgetAccess.findIndex(x => x.ResponsiveModuleID == newDisplay.ModuleID);
            if (accessIndex != -1) {
                newDisplay.ProfileID = this.widgetAccess[accessIndex].ProfileID;
                newDisplay.LoginID = this.widgetAccess[accessIndex].LoginID;
                if (this.widgetAccess[accessIndex].ProfileID != 0) {
                    newDisplay.RoleReadAccess = true;
                }
                else {
                    newDisplay.ReadAccess = true;
                }
            }

            let index = this.widgetsDisplay.findIndex(x => x.SalesPackageID == newDisplay.SalesPackageID);
            if (index != -1) {
                newDisplay.ModuleDescription = this.localizationDescriptions.get(newDisplay.ModuleName) || newDisplay.ModuleDescription;
                newDisplay.ModuleName = this.localizationNames.get(newDisplay.ModuleName) || newDisplay.ModuleName;
                this.widgetsDisplay[index].TableData.push(newDisplay);
            }
        });

        this.widgetsDisplay = this.widgetsDisplay.filter(widgetCategory => widgetCategory.TableData.length > 0);

        this.setupWidgetsGridOptions();
        this.widgetsGridOptions.rowData = this.widgetsDisplay;

    }

    /**
    * Function that is used to filter out empty rows in the widget ag-grid. Searches the row data for the matching search term
    * */
    public filterWidgetCategory() {
        this.filteredWidgetsDisplay = [];

        this.widgetsDisplay.forEach(widgetCategory => {

            for (let i = 0; i < widgetCategory.TableData.length; i++) {
                if (widgetCategory.SalesPackageName.toLowerCase().includes(this.search.toLowerCase())
                    || widgetCategory.TableData[i].ModuleName.toLowerCase().includes(this.search.toLowerCase())
                    || widgetCategory.TableData[i].ModuleDescription.toLowerCase().includes(this.search.toLowerCase())) {
                    this.filteredWidgetsDisplay.push(widgetCategory);
                    break;
                }
            }
        });

        this.hasWidgets = false;
        this.cdr.detectChanges();

        this.setupWidgetsGridOptions();
        this.widgetsGridOptions.rowData = this.filteredWidgetsDisplay;

        if (this.filteredWidgetsDisplay.length > 0) {
            this.firstRowGroup = this.filteredWidgetsDisplay[0].SalesPackageName;
        }

        this.hasWidgets = true;
        this.cdr.detectChanges();
    }
}
